import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Activity, AtSign, BadgeCheck, Bell, BookOpen, Gauge, Globe2, KeyRound, LayoutDashboard, Mail, Menu, Settings2, SlidersHorizontal, Wrench, X, Zap } from "lucide-react";
import { Brand } from "./Brand";
import { LanguageMenu } from "./LanguageMenu";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "../auth/AuthContext";
import { links } from "../config/app";
import { useApi } from "../hooks/useApi";
import { UserAvatar } from "./UserAvatar";

const navGroups = [
  { label: "nav.group.workspace", items: [
    { to: "/", key: "nav.overview", icon: LayoutDashboard, end: true },
    { to: "/apis", key: "nav.apis", icon: Zap, end: true },
    { to: "/api-keys", key: "nav.keys", icon: KeyRound }
  ] },
  { label: "nav.group.products", items: [
    { to: "/mail", key: "nav.mail", icon: Mail },
    { to: "/hide-my-email", key: "nav.hme", icon: AtSign },
    { to: "/verify", key: "nav.verify", icon: BadgeCheck },
    { to: "/apis/dns-hosting", key: "nav.dns", icon: Globe2 },
    { to: "/apis/utility", key: "nav.utility", icon: Wrench }
  ] },
  { label: "nav.group.insights", items: [
    { to: "/usage", key: "nav.usage", icon: Activity },
    { to: "/limits", key: "nav.limits", icon: Gauge },
    { to: "/events", key: "nav.events", icon: BookOpen }
  ] },
  { label: "nav.group.account", items: [
    { to: "/notifications", key: "nav.notifications", icon: Bell },
    { to: "/preferences", key: "nav.preferences", icon: SlidersHorizontal }
  ] }
] as const;

const mobileNavItems = [
  { to: "/", key: "nav.overview", icon: LayoutDashboard, end: true },
  { to: "/hide-my-email", key: "nav.hmeShort", icon: AtSign },
  { to: "/apis/utility", key: "nav.utilityShort", icon: Wrench },
  { to: "/notifications", key: "nav.notifications", icon: Bell, notifications: true }
] as const;

export function AppShell() {
  const { t } = useI18n();
  const auth = useAuth();
  const api = useApi();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountButton = useRef<HTMLButtonElement>(null);
  const accountMenu = useRef<HTMLDivElement>(null);
  const [foregroundNotice, setForegroundNotice] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); setAccountOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!accountOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { setAccountOpen(false); accountButton.current?.focus(); }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!accountMenu.current?.contains(event.target as Node) && !accountButton.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => { document.removeEventListener("keydown", onKeyDown); document.removeEventListener("pointerdown", onPointerDown); };
  }, [accountOpen]);
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let active = true;
    void import("../lib/firebase/push").then(async push => {
      const supported = await push.supportsPush();
      if (supported && active) unsubscribe = push.listenForeground(() => {
        window.dispatchEvent(new CustomEvent("kitsos:notification"));
        setForegroundNotice(true);
        window.setTimeout(() => setForegroundNotice(false), 5000);
      });
    });
    return () => { active = false; unsubscribe?.(); };
  }, []);
  useEffect(() => {
    let active = true;
    const refresh = () => void api.listNotifications(true).then(async page => { if (!active) return;const count=page.unreadCount??page.items.length;setUnreadCount(count);const push=await import("../lib/firebase/push");await Promise.all([push.syncAppBadge(count),push.closeRetractedNotifications(page.retractedIds??[])]); }).catch(() => { if (active){setUnreadCount(0);void import("../lib/firebase/push").then(push=>push.syncAppBadge(0));} });
    refresh();
    window.addEventListener("kitsos:notification", refresh);
    window.addEventListener("kitsos:notifications-changed", refresh);
    return () => { active = false; window.removeEventListener("kitsos:notification", refresh); window.removeEventListener("kitsos:notifications-changed", refresh); };
  }, [api]);

  return <div className="app-shell">
    <div className="shell-atmosphere" aria-hidden="true"><span/><span/></div>
    <a className="skip-link" href="#main">{t("shell.skip")}</a>
    <header className="topbar">
      <button className="icon-button mobile-menu" aria-label={t("shell.menu")} onClick={() => setMobileOpen(true)}><Menu/></button>
      <div className="mobile-brand"><Brand/></div>
      <div className="topbar-actions">
        <LanguageMenu/>
        <NavLink className="icon-button bell-button" to="/notifications" aria-label={unreadCount ? `${t("shell.notifications")} (${unreadCount})` : t("shell.notifications")}><Bell size={19}/>{unreadCount > 0 && <span className="notification-dot" aria-hidden="true"/>}</NavLink>
        <div className="menu-wrap">
          <button ref={accountButton} className="account-button" aria-haspopup="menu" aria-expanded={accountOpen} onClick={() => setAccountOpen(value => !value)}><UserAvatar imageUrl={auth.user?.imageUrl} name={auth.user?.fullName}/><span className="account-copy"><strong data-pii>{auth.user?.fullName}</strong><small data-pii>{auth.user?.email}</small></span></button>
          {accountOpen && <div ref={accountMenu} className="popover account-menu" role="menu"><div className="account-summary"><strong data-pii>{auth.user?.fullName}</strong><span data-pii>{auth.user?.email}</span></div><a role="menuitem" href={links.myAccount} target="_blank" rel="noreferrer"><Settings2 size={17}/>{t("common.manageAccount")}</a><button role="menuitem" onClick={() => void auth.signOut()}>{t("shell.signOut")}</button></div>}
        </div>
      </div>
    </header>
    <aside className={`sidebar ${mobileOpen ? "open" : ""}`} aria-label={t("shell.primaryNav")}>
      <div className="sidebar-brand"><Brand/><button className="icon-button mobile-close" onClick={() => setMobileOpen(false)} aria-label={t("common.close")}><X/></button></div>
      <div className="sidebar-user">
        <UserAvatar imageUrl={auth.user?.imageUrl} name={auth.user?.fullName}/>
        <span><strong data-pii>{auth.user?.fullName}</strong><small data-pii>{auth.user?.email}</small></span>
      </div>
      <nav className="side-nav">{navGroups.map(group => <section className="nav-group" key={group.label} aria-labelledby={`nav-${group.label}`}><h2 id={`nav-${group.label}`} className="nav-group-label">{t(group.label)}</h2>{group.items.map(item => <NavLink key={item.to} to={item.to} end={"end" in item ? item.end : false} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}><item.icon size={18}/><span>{t(item.key)}</span></NavLink>)}</section>)}</nav>
      <a className="sidebar-status" href={links.status} target="_blank" rel="noreferrer"><Activity className="status-link-icon" size={18}/><span><strong>{t("overview.services")}</strong><small>status.kitsos.net</small></span></a>
    </aside>
    {mobileOpen && <button className="drawer-backdrop" aria-label={t("common.close")} onClick={() => setMobileOpen(false)}/>}
    <div className="app-frame">
      <div className="offline-banner" hidden={navigator.onLine} role="status">{t("offline")}</div>
      {foregroundNotice && <div className="toast" role="status">{t("notifications.title")}</div>}
      <main id="main" tabIndex={-1}><div className="workspace-grid" aria-hidden="true"/><Outlet/></main>
      <footer className="app-footer"><span>© {new Date().getFullYear()} Kitsos</span><a href={links.status} target="_blank" rel="noreferrer">{t("common.status")}</a><a href={links.myAccount} target="_blank" rel="noreferrer">My Account</a></footer>
    </div>
    <nav className="mobile-bottom-nav" aria-label={t("shell.mobileNav")}>
      {mobileNavItems.map(item => <NavLink key={item.to} to={item.to} end={"end" in item ? item.end : false} className={({ isActive }) => `bottom-nav-item ${isActive ? "active" : ""}`}><span className="bottom-nav-icon"><item.icon/>{"notifications" in item && unreadCount > 0 && <span className="bottom-nav-dot" aria-hidden="true"/>}</span><span>{t(item.key)}</span></NavLink>)}
      <button className={`bottom-nav-item ${mobileOpen ? "active" : ""}`} type="button" aria-label={t("shell.menu")} aria-expanded={mobileOpen} onClick={() => setMobileOpen(true)}><span className="bottom-nav-icon"><Menu/></span><span>{t("shell.more")}</span></button>
    </nav>
  </div>;
}
